import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    IconButton,
    Button,
    Grid2 as Grid,
    useTheme,
    useMediaQuery,
    TextField,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import { styled } from '@mui/material/styles';
import observationApiService, { FaunaSpecies } from '../../apiService/ObservationApiService';
import dropdownApiService from '../../apiService/DropdownApiService';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { setSpeciesGroup } from '../../features/speciesGroup/speciesGroupSlice';
import { AppName, getAppName } from '../../utils/commonMethods';
import { renderSpeciesIconInverted } from '../../utils/iconHelper';
import othersIcon from '../../assets/others.svg';

interface FaunaSpeciesPopupProps {
    open: boolean;
    onClose: () => void;
    onSpeciesSelect: (species: FaunaSpecies, speciesGroupId?: string) => void;
    title?: string;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: theme.spacing(2),
        padding: 0,
        minWidth: '300px',
        maxWidth: '600px', // Increased max width for horizontal layout
        position: 'relative',
        [theme.breakpoints.up('md')]: {
            maxWidth: '700px', // Even wider on desktop
        },
    },
}));

const SpeciesButton = styled(Button)(({ theme }) => {
    const useBio = getAppName() === AppName.BioEcologie;
    const main = useBio ? theme.palette.primary.main : (theme.palette.blueColors.main ?? theme.palette.primary.main);
    return ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(3, 2),
        minHeight: '100px',
        width: '100%',
        backgroundColor: main,
        color: 'white',
        borderRadius: theme.spacing(1),
        textTransform: 'none',
        fontSize: '16px',
        fontWeight: 600,
        fontFamily: theme.typography.fontFamily,
        [theme.breakpoints.up('sm')]: {
            minHeight: '120px',
            fontSize: '18px',
        },
    })
});

const IconContainer = styled(Box)(({ theme }) => ({
    fontSize: '32px',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& img': {
        width: '40px', // Smaller width for mobile
        height: '40px', // Smaller height for mobile
        filter: 'brightness(0) invert(1)', // Make the icon white
        [theme.breakpoints.up('sm')]: {
            width: '40px', // Larger width for tablet and desktop
            height: '40px', // Larger height for tablet and desktop
        },
    },
}));

const FaunaSpeciesPopup: React.FC<FaunaSpeciesPopupProps> = ({
    open,
    onClose,
    onSpeciesSelect,
    title = 'Kies de fauna',
}) => {
    const theme = useTheme();
    const isTabletOrDesktop = useMediaQuery(theme.breakpoints.up('sm')); // sm and up for tablet/desktop
    const isBio = getAppName() === AppName.BioEcologie;
    const brandMain = isBio ? theme.palette.primary.main : (theme.palette.blueColors?.main ?? theme.palette.primary.main);

    // Redux hooks
    const dispatch = useAppDispatch();
    const persistedSpeciesData = useAppSelector((state) => state.speciesGroup.data);
    const lastUpdated = useAppSelector((state) => state.speciesGroup.lastUpdated);

    const [loading, setLoading] = useState<boolean>(false);
    const [showOthersDropdown, setShowOthersDropdown] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

    // Fetch species groups when component mounts or opens
    useEffect(() => {
        if (open) {
            // Always start fresh at the top-level chooser, regardless of how
            // the previous session was left (in case it wasn't dismissed via handleClose)
            setShowOthersDropdown(false);
            setSearchTerm('');
            setSelectedCategory(null);

            // Check if we have persisted data that's recent (less than 1 hour old)
            const oneHour = 60 * 60 * 1000;
            const isDataFresh = lastUpdated && (Date.now() - lastUpdated) < oneHour;

            if (persistedSpeciesData.length > 0 && isDataFresh) {
                // Use persisted data if available and fresh
                return;
            } else {
                // Fetch fresh data from API
                fetchSpeciesGroups();
            }
        }
    }, [open]);

    const fetchSpeciesGroups = async () => {
        setLoading(true);
        try {
            const species = await observationApiService.getSpeciesGroups();

            if (species) {
                // Store in Redux for persistence
                dispatch(setSpeciesGroup(species));
            } else {
                // Use fallback data if API fails
                const fallbackData = dropdownApiService.getFallbackFaunaSpeciesGroups();
                dispatch(setSpeciesGroup(fallbackData));
            }
        } catch (error) {
            console.error('Error fetching species groups:', error);
            // Use fallback data on error only if we don't have persisted data
            if (persistedSpeciesData.length === 0) {
                const fallbackData = dropdownApiService.getFallbackFaunaSpeciesGroups();
                dispatch(setSpeciesGroup(fallbackData));
            }
        } finally {
            setLoading(false);
        }
    };

    // Helper function to render species icon based on species ID
    const renderIcon = (speciesId: string) => {
        return renderSpeciesIconInverted(speciesId, 40, 40);
    };

    const currentOthers = persistedSpeciesData.find((s) => s.id === 'others');

    const filteredOthersSpecies =
        currentOthers?.groups?.filter((g: any) =>
            g.name.toLowerCase().includes(searchTerm.toLowerCase())
        ) ?? [];

    const handleSpeciesClick = (species: FaunaSpecies) => {
        if (species.id === 'others') {
            setShowOthersDropdown(true);
            setSelectedCategory('others');
            // Don't close the popup when Others is selected, just show the dropdown
        } else {
            // For Birds, Mammals, Bats - directly select and close
            onSpeciesSelect(species);
            handleClose();
        }
    };

    const handleOthersSpeciesSelect = (subcategory: any) => {
        const othersSpecies: FaunaSpecies = {
            id: 'others',
            name: subcategory.name, // Use the specific subcategory name
            icon: othersIcon,
            subcategory: subcategory.name,
            species_group_id: subcategory.species_group_id,
        };

        // Call onSpeciesSelect with just the species object
        onSpeciesSelect(othersSpecies);
        handleClose();
    };

    const handleClose = () => {
        onClose();
        // Reset all state when closing
        setShowOthersDropdown(false);
        setSearchTerm('');
        setSelectedCategory(null);
    };

    return (
        <StyledDialog
            open={open}
            onClose={handleClose}
            maxWidth="md" // Increased max width for horizontal layout
            fullWidth
        >
            <DialogContent sx={{ p: 3 }}>
                {/* Close Button */}
                <IconButton
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: 'grey.500',
                        zIndex: 1,
                    }}
                    size="small"
                >
                    <CloseIcon fontSize="small" />
                </IconButton>

                {/* Back Button (only show when in others dropdown) */}
                {showOthersDropdown && (
                    <IconButton
                        onClick={() => {
                            setShowOthersDropdown(false);
                            setSearchTerm('');
                            setSelectedCategory(null);
                        }}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            color: 'grey.500',
                            zIndex: 1,
                        }}
                        size="small"
                    >
                        <ArrowBackIcon fontSize="small" />
                    </IconButton>
                )}

                {/* Title */}
                <Typography
                    variant="h6"
                    component="h2"
                    align="center"
                    sx={{
                        mb: 3,
                        fontFamily: "Nunito Sans Regular700",
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                    }}
                >
                    {title}
                </Typography>

                {/* Species Selection Grid - Always shown */}
                {loading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '120px'
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        {persistedSpeciesData.map((species: FaunaSpecies) => (
                            <Grid
                                size={{ xs: 6, sm: 3 }} // 2 items per row on mobile, 4 on tablet/desktop
                                key={species.id}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                }}
                            >
                                <SpeciesButton
                                    variant="contained"
                                    onClick={() => handleSpeciesClick(species)}
                                    sx={{
                                        backgroundColor: brandMain,
                                        minHeight: isTabletOrDesktop ? '120px' : '100px',
                                        width: '100%',
                                        maxWidth: '200px',
                                        border: '2px solid transparent',
                                    }}
                                >
                                    <IconContainer>
                                        {renderIcon(species.id)}
                                    </IconContainer>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontFamily: "Nunito Sans Regular700",
                                            fontWeight: 'inherit',
                                            color: 'inherit',
                                        }}
                                    >
                                        {species.name}
                                    </Typography>
                                </SpeciesButton>
                            </Grid>
                        ))}
                    </Grid>
                )}

                {/* Others Species Dropdown - Shown below when Others is selected */}
                {showOthersDropdown && (
                    <Box sx={{ mt: 3 }}>
                        {/* Search Field */}
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Zoek categorie..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <SearchIcon sx={{ color: 'grey.400', mr: 1 }} />
                                ),
                            }}
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    fontFamily: theme.typography.fontFamily,
                                },
                            }}
                        />

                        {/* Species List */}
                        <Box
                            sx={{
                                maxHeight: '200px',
                                overflow: 'auto',
                                border: `1px solid ${theme.palette.grey[300]}`,
                                borderRadius: 2,
                            }}
                        >
                            <List sx={{ p: 0 }}>
                                {filteredOthersSpecies.length > 0 ? (
                                    filteredOthersSpecies.map((species: any, index: number) => (
                                        <ListItem key={species.species_group_id} disablePadding>
                                            <ListItemButton
                                                onClick={() => handleOthersSpeciesSelect(species)}
                                                sx={{
                                                    py: 2,
                                                    px: 3,
                                                    borderBottom: index < filteredOthersSpecies.length - 1
                                                        ? `1px solid ${theme.palette.grey[200]}`
                                                        : 'none',
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.primary.light + '20',
                                                    },
                                                }}
                                            >
                                                <ListItemText
                                                    primary={species.name}
                                                    primaryTypographyProps={{
                                                        fontFamily: theme.typography.fontFamily,
                                                        fontWeight: 500,
                                                        color: theme.palette.text.primary,
                                                    }}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    ))
                                ) : (
                                    <ListItem>
                                        <ListItemText
                                            primary="Geen resultaten gevonden"
                                            primaryTypographyProps={{
                                                fontFamily: theme.typography.fontFamily,
                                                fontStyle: 'italic',
                                                color: theme.palette.grey[500],
                                                textAlign: 'center',
                                            }}
                                        />
                                    </ListItem>
                                )}
                            </List>
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </StyledDialog>
    );
};

export default FaunaSpeciesPopup;
